import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import zxcvbn from 'zxcvbn';
import { PhoneInput } from './PhoneInput';

// Metadata for oracy categories and questions
const categories = [
  {
    title: 'Linguistic (Professional Language)',
    items: [
      { name: 'professionalVocab', label: 'I use a varied and professional vocabulary when presenting ideas.' },
      { name: 'audienceLanguage', label: 'I tailor my language to suit different audiences.' },
      { name: 'explainClearly', label: 'I can explain complex terms clearly and accurately.' },
      { name: 'supportWithExamples', label: 'I support my points with examples or evidence.' }
    ]
  },
  {
    title: 'Cognitive (Critical & Purposeful)',
    items: [
      { name: 'structuredThought', label: 'I structure my thoughts clearly before speaking.' },
      { name: 'handleQuestions', label: 'I adapt to unexpected questions with calm and logic.' },
      { name: 'avoidRambling', label: 'I stay focused and avoid filler words or rambling.' },
      { name: 'summariseIdeas', label: 'I summarise complex ideas effectively.' }
    ]
  },
  {
    title: 'Social & Emotional',
    items: [
      { name: 'confidentSpeaking', label: 'I feel confident speaking in formal or high-pressure environments.' },
      { name: 'activeListening', label: 'I listen actively and respond thoughtfully.' },
      { name: 'groupLeadership', label: 'I lead or facilitate group discussions when required.' },
      { name: 'rapportBuilding', label: 'I use communication to build rapport and engage others.' }
    ]
  },
  {
    title: 'Physical (Voice & Body Language)',
    items: [
      { name: 'voiceAdjustment', label: 'I adjust my voice appropriately when presenting or in interviews.' },
      { name: 'postureGestures', label: 'I use posture and gestures to support my points when speaking.' },
      { name: 'eyeContact', label: 'I use facial expressions and eye contact to connect with the audience.' },
      { name: 'toneVariation', label: 'I vary my tone and pace to hold interest during presentations.' }
    ]
  },
  
];

const likertOptions = [
  { value: 1, label: 'Strongly Disagree' },
  { value: 2, label: 'Disagree' },
  { value: 3, label: 'Neutral' },
  { value: 4, label: 'Agree' },
  { value: 5, label: 'Strongly Agree' }
];

// Validation schema using Yup
const schema = yup.object().shape({
  firstName: yup.string().trim().required('First Name is required').label('First Name'),
  middleName: yup.string().trim().nullable().label('Middle Name'),
  lastName: yup.string().trim().required('Last Name is required').label('Last Name'),
  dateOfBirth: yup
    .date()
    .max(new Date(), "Date of Birth can't be in the future")
    .required('Date of Birth is required')
    .label('Date of Birth'),
  gender: yup
    .string()
    .oneOf(['Male', 'Female', 'Other'])
    .required('Gender is required')
    .label('Gender'),
  nationality: yup.string().trim().required('Nationality is required').label('Nationality'),
  residentialAddress: yup
    .string()
    .trim()
    .required('Residential Address is required')
    .label('Residential Address'),
  townCity: yup.string().trim().required('Town/City is required').label('Town/City'),
  countyState: yup.string().trim().required('County/State is required').label('County/State'),
  postcode: yup.string().trim().required('Postcode is required').label('Postcode'),
  email: yup
    .string()
    .trim()
    .email('Invalid email address')
    .required('Email is required')
    .label('Email Address'),
  password: yup
    .string()
    .required('Password is required')
    .min(8, 'Password must be at least 8 characters')
    .label('Password'),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref('password'), null], 'Passwords must match')
    .required('Confirm Password is required')
    .label('Confirm Password'),
  contactNumber: yup
    .string()
    .matches(/^\+?\d{10,14}$/, 'Contact Number must be 10-14 digits')
    .required('Contact Number is required')
    .label('Contact Number'),
  currentYearGrade: yup
    .string()
    .trim()
    .required('Current Year/Grade is required')
    .label('Current Year/Grade'),
  schoolName: yup.string().trim().required('School Name is required').label('School Name'),
  schoolAddress: yup
    .string()
    .trim()
    .required('School Address is required')
    .label('School Address'),
  parentFullName: yup
    .string()
    .trim()
    .required('Parent/Guardian Full Name is required')
    .label('Parent/Guardian Full Name'),
  parentOccupation: yup
    .string()
    .trim()
    .required('Parent/Guardian Occupation is required')
    .label('Parent/Guardian Occupation'),
  parentContactNumber: yup
    .string()
    .matches(/^\+?\d{10,14}$/, 'Contact Number must be 10-14 digits')
    .required('Parent/Guardian Contact Number is required')
    .label('Parent/Guardian Contact Number'),
  parentEmail: yup
    .string()
    .trim()
    .email('Invalid email address')
    .required('Parent/Guardian Email is required')
    .label('Parent/Guardian Email'),
  declaration: yup
    .boolean()
    .oneOf([true], 'You must agree to the terms and conditions')
    .required('Declaration is required')
    .label('Declaration'),
  signature: yup.string().trim().required('Signature is required').label('Signature'),
  // Add Oracy fields validation
  ...categories.reduce((acc, cat) => {
    cat.items.forEach(({ name }) => {
      acc[name] = yup.number().min(1).max(5).nullable();
    });
    return acc;
  }, {})
});

const steps = [
  'Student Information',
  'Parent/Guardian Details',
  'Declaration & Consent',
];

const stepFields = [
  ['firstName','middleName','lastName','dateOfBirth','gender','nationality','residentialAddress','townCity','countyState','postcode','email','password','confirmPassword','contactNumber','currentYearGrade','schoolName','schoolAddress'],
  ['parentFullName','parentOccupation','parentContactNumber','parentEmail'],
  ['declaration','signature'],
];

export default function StudentRegistration() {
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [isVerifyingEmail, setIsVerifyingEmail] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [assessmentSubmitting, setAssessmentSubmitting] = useState(false);
  const [assessmentSubmitted, setAssessmentSubmitted] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(null);

  // Registration form
  const {
    register,
    handleSubmit,
    formState: { errors },
    trigger,
    setError,
    clearErrors,
    getValues,
    setValue,
    reset,
    control,
  } = useForm({
    mode: 'onChange',
    resolver: yupResolver(schema),
    criteriaMode: 'all',
    reValidateMode: 'onChange',
  });

  // Auto-fill signature with full name when on signature step
  useEffect(() => {
    if (step === 2) {
      const firstName = getValues('firstName') || '';
      const middleName = getValues('middleName') || '';
      const lastName = getValues('lastName') || '';
      const fullName = [firstName, middleName, lastName].filter(Boolean).join(' ');
      if (!getValues('signature') && fullName.trim()) {
        setValue('signature', fullName.trim());
      }
    }
  }, [step, getValues, setValue]);

  const validateStep = async () => {
    const fields = stepFields[step];
    return await trigger(fields);
  };

  const handleNext = async () => {
    const isValid = await validateStep();
    if (isValid) {
      setStep((prev) => prev + 1);
    }
  };

  const handleBack = () => setStep((prev) => prev - 1);

  const onSubmit = async (data) => {
    setSubmitting(true);
    try {
      // Transform the data to match backend field names and exclude oracy scores
      const transformedData = {
        first_name: data.firstName,
        middle_name: data.middleName,
        last_name: data.lastName,
        date_of_birth: data.dateOfBirth,
        gender: data.gender,
        nationality: data.nationality,
        residential_address: data.residentialAddress,
        town_city: data.townCity,
        county_state: data.countyState,
        postcode: data.postcode,
        email: data.email,
        password: data.password,
        contact_number: data.contactNumber,
        current_year_grade: data.currentYearGrade,
        school_name: data.schoolName,
        school_address: data.schoolAddress,
        parent_full_name: data.parentFullName,
        parent_occupation: data.parentOccupation,
        parent_contact_number: data.parentContactNumber,
        parent_email: data.parentEmail,
        declaration_accepted: data.declaration,
        signature: data.signature,
      };

      const response = await fetch('https://server.oracy.uk/api/saveStudentRegistration', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(transformedData),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Registration failed. Please try again.');
      }

      setRegisteredEmail(data.email);
      setSubmitted(true);
      reset();
    } catch (error) {
      let errorMessage = 'Registration failed. Please try again later.';
      if (error.name === 'TypeError' && error.message === 'Failed to fetch') {
        errorMessage = 'Unable to connect to the server. Please check your internet connection or try again later.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      alert(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="mx-auto mt-20 max-w-lg bg-white p-6 sm:p-10 rounded-2xl shadow-xl text-center">
        <h2 className="text-2xl sm:text-3xl font-bold text-green-600 mb-2">Registration Complete!</h2>
        <p className="text-gray-600 mb-6">Thank you for registering. We have received your details.</p>
        <a
          href="/login"
          className="inline-block mb-6 px-6 py-2 rounded-lg bg-orange-600 text-white hover:bg-orange-700 font-semibold"
        >
          Login to your account
        </a>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      noValidate
      className="mx-auto mt-8 mb-8 w-full max-w-lg sm:max-w-3xl px-2 sm:px-8 py-6 sm:py-10 bg-white rounded-2xl shadow-xl space-y-8"
    >
      {/* Progress Bar/Stepper */}
      <div className="flex items-center justify-between mb-8">
        {steps.map((label, idx) => (
          <div key={label} className="flex-1 flex flex-col items-center">
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-white mb-1
                ${step === idx ? 'bg-orange-600' : step > idx ? 'bg-green-500' : 'bg-gray-300'}`}
            >
              {idx + 1}
            </div>
            <span className={`text-xs text-center ${step === idx ? 'text-orange-600 font-semibold' : 'text-gray-500'}`}>{label}</span>
            {idx < steps.length - 1 && (
              <div className="h-1 w-full bg-gray-200 mt-1 mb-1">
                <div
                  className={`h-1 ${step > idx ? 'bg-green-500' : 'bg-gray-200'}`}
                  style={{ width: '100%' }}
                />
              </div>
            )}
          </div>
        ))}
      </div>

      {step === 3 ? (
        // Render categorized Oracy questions
        <div className="space-y-8 sm:space-y-10">
          <div>
            <span className="inline-block rounded-full bg-orange-100 text-orange-700 font-bold px-3 py-1 text-sm">Assessment Is Optional</span>
          </div>
          {categories.map(cat => (
            <div key={cat.title} className="border-t pt-4 sm:pt-6">
              <h2 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4 text-orange-600">{cat.title}</h2>
              <div className="space-y-4 sm:space-y-6">
                {cat.items.map(q => (
                  <div key={q.name} className="mb-2 sm:mb-4">
                    <p className="mb-1 sm:mb-2 font-medium text-gray-700 text-sm sm:text-base">{q.label}</p>
                    <div className="flex flex-wrap gap-2 sm:gap-4">
                      {likertOptions.map(opt => (
                        <label key={opt.value} className="flex items-center space-x-1 sm:space-x-2">
                          <input
                            type="radio"
                            value={opt.value}
                            {...register(q.name)}
                            className="form-radio text-orange-600"
                          />
                          <span className="text-xs sm:text-sm">{opt.label}</span>
                        </label>
                      ))}
                    </div>
                    {errors[q.name] && (
                      <p className="text-red-600 text-xs sm:text-sm mt-1">{errors[q.name].message}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        // Regular form fields
        <div className="space-y-4 sm:space-y-6">
          {stepFields[step].map((name) => {
            const isCheckbox = name === 'declaration';
            const label = schema.fields[name].spec.label || name.split(/(?=[A-Z])/).join(' ');
            
            if (name === 'password') {
              return (
                <div key={name} className="mb-1 sm:mb-2">
                  <label htmlFor={name} className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                    {label}
                  </label>
                  <div className="relative">
                    <input
                      id={name}
                      type={showPassword ? 'text' : 'password'}
                      {...register(name)}
                      onChange={e => {
                        setValue(name, e.target.value);
                        setPasswordStrength(zxcvbn(e.target.value));
                      }}
                      className="mt-1 block w-full rounded-lg border border-gray-300 p-2 focus:border-orange-500 focus:outline-none text-xs sm:text-sm pr-10"
                    />
                    <button
                      type="button"
                      tabIndex={-1}
                      className="absolute inset-y-0 right-0 px-3 flex items-center text-gray-500"
                      onClick={() => setShowPassword(v => !v)}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? 'Hide' : 'Show'}
                    </button>
                  </div>
                  {passwordStrength && (
                    <div className="mt-1 text-xs">
                      <span>Password strength: </span>
                      <span className={
                        passwordStrength.score < 2 ? 'text-red-600' :
                        passwordStrength.score === 2 ? 'text-yellow-600' :
                        'text-green-600'
                      }>
                        {['Very Weak', 'Weak', 'Fair', 'Good', 'Strong'][passwordStrength.score]}
                      </span>
                    </div>
                  )}
                  {errors[name] && (
                    <p className="mt-1 text-xs sm:text-sm text-red-600">{errors[name].message}</p>
                  )}
                </div>
              );
            }
            if (name === 'confirmPassword') {
              return (
                <div key={name} className="mb-1 sm:mb-2">
                  <label htmlFor={name} className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                    {label}
                  </label>
                  <div className="relative">
                    <input
                      id={name}
                      type={showConfirmPassword ? 'text' : 'password'}
                      {...register(name)}
                      className="mt-1 block w-full rounded-lg border border-gray-300 p-2 focus:border-orange-500 focus:outline-none text-xs sm:text-sm pr-10"
                    />
                    <button
                      type="button"
                      tabIndex={-1}
                      className="absolute inset-y-0 right-0 px-3 flex items-center text-gray-500"
                      onClick={() => setShowConfirmPassword(v => !v)}
                      aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                    >
                      {showConfirmPassword ? 'Hide' : 'Show'}
                    </button>
                  </div>
                  {errors[name] && (
                    <p className="mt-1 text-xs sm:text-sm text-red-600">{errors[name].message}</p>
                  )}
                </div>
              );
            }
            if (name === 'contactNumber' || name === 'parentContactNumber') {
              return (
                <Controller
                  key={name}
                  name={name}
                  control={control}
                  rules={{
                    required: `${label} is required`,
                    pattern: {
                      value: /^\+?\d{10,14}$/,
                      message: 'Contact Number must be 10-14 digits'
                    }
                  }}
                  render={({ field, fieldState }) => (
                    <div className="mb-1 sm:mb-2 relative">
                      <label htmlFor={name} className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                        {label}
                      </label>
                      <PhoneInput
                        id={name}
                        {...field}
                        onChange={field.onChange}
                        aria-invalid={!!fieldState.error}
                        aria-describedby={fieldState.error ? `${name}-error` : undefined}
                        className={`mt-1 w-full rounded-lg border p-2 focus:border-orange-500 focus:outline-none text-xs sm:text-sm ${fieldState.error ? 'border-red-500' : 'border-gray-300'}`}
                      />
                      {!fieldState.error && field.value && (
                        <span className="absolute right-2 top-1/2 transform -translate-y-1/2 text-green-600" aria-label="Valid input">✔</span>
                      )}
                      {fieldState.error && (
                        <p id={`${name}-error`} className="mt-1 text-xs sm:text-sm text-red-600">{fieldState.error.message}</p>
                      )}
                    </div>
                  )}
                />
              );
            }
            if (name === 'dateOfBirth') {
              return (
                <div key={name} className="mb-1 sm:mb-2 relative">
                  <label htmlFor={name} className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                    {label}
                  </label>
                  <input
                    id={name}
                    type="date"
                    {...register(name, {
                      onBlur: () => trigger(name),
                      onChange: () => trigger(name),
                    })}
                    aria-invalid={!!errors[name]}
                    aria-describedby={errors[name] ? `${name}-error` : undefined}
                    className={`mt-1 block w-full rounded-lg border p-2 focus:border-orange-500 focus:outline-none text-xs sm:text-sm ${errors[name] ? 'border-red-500' : 'border-gray-300'} pr-10`}
                  />
                  {!errors[name] && getValues(name) && (
                    <span className="absolute right-2 top-1/2 transform -translate-y-1/2 text-green-600" aria-label="Valid input">✔</span>
                  )}
                  {errors[name] && (
                    <p id={`${name}-error`} className="mt-1 text-xs sm:text-sm text-red-600">{errors[name].message}</p>
                  )}
                </div>
              );
            }
            if (schema.fields[name].type === 'string' && ['gender'].includes(name)) {
              return (
                <div key={name} className="mb-1 sm:mb-2">
                  <label htmlFor={name} className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                    {label}
                  </label>
                  <div className="relative">
                    <select
                      id={name}
                      {...register(name)}
                      className="mt-1 block w-full rounded-lg border border-gray-300 p-2 focus:border-orange-500 focus:outline-none text-xs sm:text-sm"
                    >
                      <option value="">Select Gender</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  {errors[name] && (
                    <p className="mt-1 text-xs sm:text-sm text-red-600">{errors[name].message}</p>
                  )}
                </div>
              );
            }
            if (schema.fields[name].type === 'string' && name.includes('Address')) {
              return (
                <div key={name} className="mb-1 sm:mb-2">
                  <label htmlFor={name} className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                    {label}
                  </label>
                  <div className="relative">
                    <input
                      id={name}
                      type="text"
                      {...register(name)}
                      aria-invalid={!!errors[name]}
                      aria-describedby={errors[name] ? `${name}-error` : undefined}
                      className={`mt-1 block w-full rounded-lg border p-2 focus:border-orange-500 focus:outline-none text-xs sm:text-sm ${errors[name] ? 'border-red-500' : 'border-gray-300'}`}
                    />
                  </div>
                  {errors[name] && (
                    <p id={`${name}-error`} className="mt-1 text-xs sm:text-sm text-red-600">{errors[name].message}</p>
                  )}
                </div>
              );
            }
            if (isCheckbox) {
              return (
                <div key={name} className="mb-1 sm:mb-2">
                  <div className="relative flex items-center">
                    <input
                      id={name}
                      type="checkbox"
                      {...register(name)}
                      className="mr-2 h-5 w-5 rounded border border-gray-300 focus:border-orange-500 focus:outline-none"
                    />
                    <label htmlFor={name} className="text-xs sm:text-sm font-medium text-gray-700 select-none align-middle">
                      I agree to the{' '}
                      <a href="/termsandconditions" target="_blank" rel="noopener noreferrer" className="text-orange-600 underline">terms and conditions</a>{' '}and{' '}
                      <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-orange-600 underline">privacy policy</a>.
                    </label>
                  </div>
                  {errors[name] && (
                    <p className="mt-1 text-xs sm:text-sm text-red-600">{errors[name].message}</p>
                  )}
                </div>
              );
            }
            return (
              <div key={name} className="mb-1 sm:mb-2 relative">
                {!isCheckbox && (
                  <label htmlFor={name} className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                    {label}
                  </label>
                )}
                <div className="relative">
                  <input
                    id={name}
                    type={name.includes('Date') ? 'date' : name.includes('Password') ? 'password' : name === 'email' ? 'email' : 'text'}
                    {...register(name, {
                      onBlur: () => trigger(name),
                      onChange: () => trigger(name),
                    })}
                    aria-invalid={!!errors[name]}
                    aria-describedby={errors[name] ? `${name}-error` : undefined}
                    className={`mt-1 block w-full rounded-lg border p-2 focus:border-orange-500 focus:outline-none text-xs sm:text-sm ${errors[name] ? 'border-red-500' : 'border-gray-300'} pr-10`}
                  />
                  {/* Success indicator */}
                  {!errors[name] && getValues(name) && (
                    <span className="absolute right-2 top-1/2 transform -translate-y-1/2 text-green-600" aria-label="Valid input">✔</span>
                  )}
                </div>
                {errors[name] && (
                  <p id={`${name}-error`} className="mt-1 text-xs sm:text-sm text-red-600">{errors[name].message}</p>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="flex flex-col sm:flex-row justify-between mt-8 gap-4">
        {step > 0 && (
          <button
            type="button"
            onClick={handleBack}
            className="px-5 py-2 rounded-lg bg-gray-200 hover:bg-gray-300 font-medium w-full sm:w-auto"
          >
            Back
          </button>
        )}
        {step < steps.length - 1 ? (
          <>
            <button
              type="button"
              onClick={handleNext}
              className="ml-auto px-6 py-2 rounded-lg bg-orange-600 text-white hover:bg-orange-700 font-semibold disabled:opacity-50 w-full sm:w-auto"
            >
              Next
            </button>
          </>
        ) : (
          <button
            type="submit"
            disabled={submitting}
            className="ml-auto px-6 py-2 rounded-lg bg-green-600 text-white hover:bg-green-700 font-semibold disabled:opacity-50 w-full sm:w-auto"
          >
            {submitting ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Submitting...
              </span>
            ) : (
              'Submit'
            )}
          </button>
        )}
      </div>
    </form>
  );
}
